import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Church, MapPin, Users, CalendarDays, Plus, Clock, Megaphone, Globe, Check, HelpCircle, X, Mail, Phone, ExternalLink, Cross, Sun, BookOpen, PenSquare, MessageCircle, Search, HandHeart, Settings, Loader2, UserPlus, Shield, Link2, Pencil, Trash2, Repeat, Camera, ImagePlus, Send, Building2 } from "lucide-react";
import { getParish, getParishes, getEvents, getParishEvents, createEvent, updateEvent, deleteEvent, createParish, requestMembership, rsvpEvent, getEventAttendees, getParishMembers, getMyPendingParishRequests, getRecurringEvents, createRecurringEvent, updateRecurringEvent, deleteRecurringEvent, updateParish, getParishPosts, createPost, uploadImage, resolveImageUrl, likePost, updatePost, deletePost, getMyBusinessListings } from "@/lib/api";
import { useNavigate, useParams } from "react-router-dom";
import { SafeHTML } from "@/components/SafeHTML";
import { format } from "date-fns";
import { MembershipBanner } from "@/components/MembershipModal";
import { PostCard } from "@/pages/DashboardPage";
import ChallengesTab from "@/pages/ChallengesTab";

// Default banner image for parishes
const DEFAULT_PARISH_BANNER = "https://customer-assets.emergentagent.com/job_5ceb40c7-1f38-40ab-be74-e959af30b7bb/artifacts/z612tw6f_diocesisdecordoba-2430e773095f66f1fb2c5fba8a0c6041-cathopic.jpg";

// Check if user is admin of a parish
function isParishAdmin(user, parishId) {
  if (!user) return false;
  // Site admins can manage any parish
  if (user.is_site_admin || user.role === "admin") return true;
  // Parish admins can manage their parish
  if (user.is_parish_admin || user.role === "parish_admin") {
    if (user.parish_id === parishId) return true;
    if (user.admin_parish_ids?.includes(parishId)) return true;
  }
  return false;
}

function CreateEventDialog({ user, onCreated, parishId }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState(null);
  const [eventTime, setEventTime] = useState("10:00");
  const [location, setLocation] = useState("");
  const [privacy, setPrivacy] = useState("public");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleSubmit = async () => {
    if (!title || !description || !eventDate || !location) {
      toast.error("Fill in all fields");
      return;
    }
    setLoading(true);
    try {
      // Combine date and time
      const [hours, minutes] = eventTime.split(':').map(Number);
      const combinedDate = new Date(eventDate);
      combinedDate.setHours(hours, minutes, 0, 0);
      
      await createEvent({
        title, description,
        event_date: combinedDate.toISOString(),
        location,
        parish_id: parishId || user.parish_id,
        privacy
      });
      toast.success("Event created!");
      setOpen(false);
      setTitle(""); setDescription(""); setEventDate(null); setEventTime("10:00"); setLocation(""); setPrivacy("public");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create event");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold text-sm" data-testid="create-event-btn">
          <Plus className="w-4 h-4 mr-1" /> New Event
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26]">Create Event</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Sunday Potluck" className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="event-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Description</Label>
            <Textarea value={description} onChange={e => { setDescription(e.target.value); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }} placeholder="Event details... (Enter for new paragraph)" className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[80px] max-h-[300px] overflow-y-auto resize-none" data-testid="event-description-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Date</Label>
              <Popover modal={true}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-['Nunito'] text-sm border-[#E5E0D5] text-[#2D2A26]" data-testid="event-date-picker">
                    <CalendarDays className="w-4 h-4 mr-2 text-[#6B665E]" />
                    {eventDate ? format(eventDate, "MMM d") : "Pick"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-[#E5E0D5] z-[9999]" align="start">
                  <Calendar mode="single" selected={eventDate} onSelect={setEventDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Time</Label>
              <Input 
                type="time" 
                value={eventTime} 
                onChange={e => setEventTime(e.target.value)} 
                className="border-[#E5E0D5] font-['Nunito'] text-sm" 
                data-testid="event-time-input" 
              />
            </div>
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Location</Label>
              <Input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Parish Hall" className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="event-location-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Privacy</Label>
            <Select value={privacy} onValueChange={setPrivacy}>
              <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="event-privacy-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="public" className="font-['Nunito']">Public - Anyone can see</SelectItem>
                <SelectItem value="private" className="font-['Nunito']">Private - Invite only</SelectItem>
                <SelectItem value="parish_only" className="font-['Nunito']">Parish Only - Parish members</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleSubmit} disabled={loading} className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold active:scale-95" data-testid="event-submit-btn">
            {loading ? "Creating..." : "Create Event"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditEventDialog({ event, onUpdated, onClose }) {
  const [title, setTitle] = useState(event.title);
  const [description, setDescription] = useState(event.description);
  const [eventDate, setEventDate] = useState(new Date(event.event_date));
  const [eventTime, setEventTime] = useState(
    new Date(event.event_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  );
  const [location, setLocation] = useState(event.location);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!title || !description || !eventDate || !location) {
      toast.error("Fill in all fields");
      return;
    }
    setLoading(true);
    try {
      // Combine date and time
      const [hours, minutes] = eventTime.split(':').map(Number);
      const combinedDate = new Date(eventDate);
      combinedDate.setHours(hours, minutes, 0, 0);
      
      await updateEvent(event.event_id, {
        title,
        description,
        event_date: combinedDate.toISOString(),
        location
      });
      toast.success("Event updated!");
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update event");
    }
    setLoading(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26]">Edit Event</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="edit-event-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Description</Label>
            <Textarea value={description} onChange={e => { setDescription(e.target.value); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }} className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[80px] max-h-[300px] overflow-y-auto resize-none" data-testid="edit-event-description-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Date</Label>
              <Popover modal={true}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-['Nunito'] text-sm border-[#E5E0D5] text-[#2D2A26]" data-testid="edit-event-date-picker">
                    <CalendarDays className="w-4 h-4 mr-2 text-[#6B665E]" />
                    {eventDate ? format(eventDate, "MMM d") : "Pick"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-[#E5E0D5] z-[9999]" align="start">
                  <Calendar mode="single" selected={eventDate} onSelect={setEventDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Time</Label>
              <Input 
                type="time" 
                value={eventTime} 
                onChange={e => setEventTime(e.target.value)} 
                className="border-[#E5E0D5] font-['Nunito'] text-sm" 
                data-testid="edit-event-time-input" 
              />
            </div>
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Location</Label>
              <Input value={location} onChange={e => setLocation(e.target.value)} className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="edit-event-location-input" />
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={loading} className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold" data-testid="save-event-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddRecurringEventDialog({ parishId, onCreated, onClose }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [schedule, setSchedule] = useState("");
  const [time, setTime] = useState("10:00 AM");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!title || !description || !schedule || !time || !location) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await createRecurringEvent({
        title,
        description,
        schedule,
        time,
        location,
        parish_id: parishId
      });
      toast.success("Recurring event created!");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create recurring event");
    }
    setLoading(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26] flex items-center gap-2">
            <Repeat className="w-5 h-5 text-[var(--liturgical-primary)]" />
            Add Recurring Event
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Title</Label>
            <Input 
              value={title} 
              onChange={e => setTitle(e.target.value)} 
              placeholder="e.g. First Friday Adoration"
              className="border-[#E5E0D5] font-['Nunito'] text-sm" 
            />
          </div>
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Description</Label>
            <Textarea 
              value={description} 
              onChange={e => setDescription(e.target.value)} 
              placeholder="Details about this recurring event..."
              className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[60px]" 
            />
          </div>
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Schedule</Label>
            <Input 
              value={schedule} 
              onChange={e => setSchedule(e.target.value)} 
              placeholder="e.g. Every First Friday, Weekly, etc."
              className="border-[#E5E0D5] font-['Nunito'] text-sm" 
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Time</Label>
              <Input 
                value={time} 
                onChange={e => setTime(e.target.value)} 
                placeholder="e.g. 7:00 PM"
                className="border-[#E5E0D5] font-['Nunito'] text-sm" 
              />
            </div>
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Location</Label>
              <Input 
                value={location} 
                onChange={e => setLocation(e.target.value)} 
                placeholder="e.g. Chapel"
                className="border-[#E5E0D5] font-['Nunito'] text-sm" 
              />
            </div>
          </div>
          <Button 
            onClick={handleSubmit} 
            disabled={loading} 
            className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Add Recurring Event
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditRecurringEventDialog({ event, onUpdated, onClose }) {
  const [title, setTitle] = useState(event.title);
  const [description, setDescription] = useState(event.description);
  const [schedule, setSchedule] = useState(event.schedule);
  const [time, setTime] = useState(event.time);
  const [location, setLocation] = useState(event.location);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!title || !description || !schedule || !time || !location) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await updateRecurringEvent(event.recurring_event_id, {
        title,
        description,
        schedule,
        time,
        location
      });
      toast.success("Recurring event updated!");
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setLoading(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26] flex items-center gap-2">
            <Repeat className="w-5 h-5 text-[var(--liturgical-primary)]" />
            Edit Recurring Event
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Title</Label>
            <Input 
              value={title} 
              onChange={e => setTitle(e.target.value)} 
              className="border-[#E5E0D5] font-['Nunito'] text-sm" 
            />
          </div>
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Description</Label>
            <Textarea 
              value={description} 
              onChange={e => setDescription(e.target.value)} 
              className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[60px]" 
            />
          </div>
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Schedule</Label>
            <Input 
              value={schedule} 
              onChange={e => setSchedule(e.target.value)} 
              placeholder="e.g. Every First Friday, Weekly, etc."
              className="border-[#E5E0D5] font-['Nunito'] text-sm" 
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Time</Label>
              <Input 
                value={time} 
                onChange={e => setTime(e.target.value)} 
                className="border-[#E5E0D5] font-['Nunito'] text-sm" 
              />
            </div>
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Location</Label>
              <Input 
                value={location} 
                onChange={e => setLocation(e.target.value)} 
                className="border-[#E5E0D5] font-['Nunito'] text-sm" 
              />
            </div>
          </div>
          <Button 
            onClick={handleSubmit} 
            disabled={loading} 
            className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ViewRecurringEventDialog({ event, onClose }) {
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26] flex items-center gap-2">
            <Repeat className="w-5 h-5 text-[var(--liturgical-primary)]" />
            {event.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-[var(--liturgical-primary)]/30 text-[var(--liturgical-primary)] text-xs">
              <Repeat className="w-3 h-3 mr-1" />
              {event.schedule}
            </Badge>
          </div>
          
          <div className="p-4 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5]">
            <p className="text-sm text-[#2D2A26] font-['Nunito'] whitespace-pre-wrap">{event.description}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2 text-sm text-[#6B665E] font-['Nunito']">
              <Clock className="w-4 h-4 text-[var(--liturgical-primary)]" />
              <span>{event.time}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#6B665E] font-['Nunito']">
              <MapPin className="w-4 h-4 text-[var(--liturgical-primary)]" />
              <span>{event.location}</span>
            </div>
          </div>
          
          <Button 
            onClick={onClose}
            variant="outline"
            className="w-full border-[#E5E0D5] font-['Nunito']"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EventCard({ event, onRsvp, canEdit, onEdit, onDelete }) {
  const [showAttendees, setShowAttendees] = useState(false);
  const [attendees, setAttendees] = useState({ going: [], maybe: [] });
  const [loadingAttendees, setLoadingAttendees] = useState(false);

  const loadAttendees = async () => {
    if (!showAttendees) {
      setLoadingAttendees(true);
      try {
        const res = await getEventAttendees(event.event_id);
        setAttendees(res.data);
      } catch {}
      setLoadingAttendees(false);
    }
    setShowAttendees(!showAttendees);
  };

  return (
    <Card className="bg-white border-[#E5E0D5] shadow-sm" data-testid={`event-card-${event.event_id}`}>
      <CardContent className="p-5">
        <div className="flex gap-4">
          <div className="w-14 h-14 bg-[var(--liturgical-primary)]/10 rounded-md flex flex-col items-center justify-center flex-shrink-0">
            <span className="text-[10px] font-['Nunito'] text-[var(--liturgical-primary)] uppercase font-semibold">
              {new Date(event.event_date).toLocaleDateString('en', { month: 'short' })}
            </span>
            <span className="text-lg font-['Crimson_Text'] font-bold text-[var(--liturgical-primary)]">
              {new Date(event.event_date).getDate()}
            </span>
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]">{event.title}</h3>
              <div className="flex items-center gap-2">
                {event.scope === "global" && (
                  <Badge variant="outline" className="border-[var(--liturgical-secondary)] text-[var(--liturgical-secondary)] text-[10px] font-['Nunito']">
                    <Globe className="w-2.5 h-2.5 mr-1" /> Public
                  </Badge>
                )}
                {canEdit && (
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-[#6B665E] hover:text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10"
                      onClick={() => onEdit(event)}
                      data-testid={`edit-event-${event.event_id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-[#6B665E] hover:text-red-600 hover:bg-red-50"
                      onClick={() => onDelete(event.event_id)}
                      data-testid={`delete-event-${event.event_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1 whitespace-pre-wrap">{event.description}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-[#6B665E] font-['Nunito']">
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(event.event_date).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#E5E0D5]/50">
          <button
            onClick={loadAttendees}
            className="text-xs font-['Nunito'] text-[#6B665E] hover:text-[#2D2A26]"
            data-testid={`event-attendees-${event.event_id}`}
          >
            <Users className="w-3.5 h-3.5 inline mr-1" />
            {event.rsvp_count || 0} going{event.maybe_count > 0 && `, ${event.maybe_count} maybe`}
          </button>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={event.user_rsvp === "going" ? "default" : "outline"}
              onClick={() => onRsvp(event.event_id, event.user_rsvp === "going" ? "not_going" : "going")}
              className={`h-7 text-xs font-['Nunito'] ${event.user_rsvp === "going" ? 'bg-[var(--liturgical-primary)] hover:brightness-90 text-white' : 'border-[#E5E0D5] text-[#6B665E] hover:bg-[var(--liturgical-primary)]/10 hover:text-[var(--liturgical-primary)] hover:border-[var(--liturgical-primary)]'}`}
              data-testid={`event-going-${event.event_id}`}
            >
              <Check className="w-3 h-3 mr-1" /> Going
            </Button>
            <Button
              size="sm"
              variant={event.user_rsvp === "maybe" ? "default" : "outline"}
              onClick={() => onRsvp(event.event_id, event.user_rsvp === "maybe" ? "not_going" : "maybe")}
              className={`h-7 text-xs font-['Nunito'] ${event.user_rsvp === "maybe" ? 'bg-[var(--liturgical-secondary)] hover:brightness-90 text-[#2D2A26]' : 'border-[#E5E0D5] text-[#6B665E] hover:bg-[var(--liturgical-secondary)]/10 hover:text-[var(--liturgical-secondary)] hover:border-[var(--liturgical-secondary)]'}`}
              data-testid={`event-maybe-${event.event_id}`}
            >
              <HelpCircle className="w-3 h-3 mr-1" /> Maybe
            </Button>
          </div>
        </div>
        {showAttendees && (
          <div className="mt-4 pt-3 border-t border-[#E5E0D5]/50 space-y-3">
            {loadingAttendees ? (
              <p className="text-xs text-[#6B665E] font-['Nunito'] text-center">Loading...</p>
            ) : (
              <>
                {attendees.going.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-[var(--liturgical-primary)] font-['Nunito'] mb-2">Going ({attendees.going.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {attendees.going.map(u => (
                        <div key={u.user_id} className="flex items-center gap-1.5 bg-[#F9F7F2] rounded-full px-2 py-1">
                          <Avatar className="h-5 w-5 border border-[#E5E0D5]">
                            <AvatarImage src={u.picture} />
                            <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-[8px]">{u.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <span className="text-xs font-['Nunito'] text-[#2D2A26]">{u.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {attendees.maybe.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-[var(--liturgical-secondary)] font-['Nunito'] mb-2">Maybe ({attendees.maybe.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {attendees.maybe.map(u => (
                        <div key={u.user_id} className="flex items-center gap-1.5 bg-[#F9F7F2] rounded-full px-2 py-1">
                          <Avatar className="h-5 w-5 border border-[#E5E0D5]">
                            <AvatarImage src={u.picture} />
                            <AvatarFallback className="bg-[var(--liturgical-secondary)] text-white text-[8px]">{u.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <span className="text-xs font-['Nunito'] text-[#2D2A26]">{u.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {attendees.going.length === 0 && attendees.maybe.length === 0 && (
                  <p className="text-xs text-[#6B665E] font-['Nunito'] text-center">No RSVPs yet</p>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CreateParishDialog({ user, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Only admins can create parishes
  const canCreate = user?.role === "admin" || user?.role === "parish_admin";
  
  if (!canCreate) return null;

  const handleSubmit = async () => {
    if (!name || !description || !location) { toast.error("Fill in all fields"); return; }
    setLoading(true);
    try {
      await createParish({ name, description, location });
      toast.success("Parish created! You are now the administrator.");
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create parish");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-[var(--liturgical-secondary)] text-[var(--liturgical-secondary)] hover:bg-[var(--liturgical-secondary)]/10 font-['Nunito'] font-semibold text-sm" data-testid="create-parish-btn">
          <Plus className="w-4 h-4 mr-1" /> Create Parish
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26]">Register a Parish</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs">Parish Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. St. Mary's Cathedral" className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="parish-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs">Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Tell us about your parish..." className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="parish-description-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs">Location</Label>
            <Input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. 123 Main St, City" className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="parish-location-input" />
          </div>
          <Button onClick={handleSubmit} disabled={loading} className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold active:scale-95" data-testid="parish-submit-btn">
            {loading ? "Creating..." : "Create Parish"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ParishPage({ user, isPublic = false }) {
  const navigate = useNavigate();
  const { parishId: urlParishId } = useParams();
  const [parish, setParish] = useState(null);
  const [allParishes, setAllParishes] = useState([]);
  const [events, setEvents] = useState([]);
  const [recurringEvents, setRecurringEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("about");
  const [pendingRequests, setPendingRequests] = useState([]);
  const [requestingJoin, setRequestingJoin] = useState(false);
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [bannerUrl, setBannerUrl] = useState("");
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [parishPosts, setParishPosts] = useState([]);
  const [newPostContent, setNewPostContent] = useState("");
  const [newPostImage, setNewPostImage] = useState(null);
  const [postingToParish, setPostingToParish] = useState(false);
  const [postAsAdmin, setPostAsAdmin] = useState(false);
  const [userBusinesses, setUserBusinesses] = useState([]);
  const [postAsBusiness, setPostAsBusiness] = useState(null);

  // Use URL parishId if provided, otherwise use user's parish
  const targetParishId = urlParishId || user?.parish_id;
  const isViewingOwnParish = !urlParishId || urlParishId === user?.parish_id;
  
  // Check if user is admin of THIS specific parish (only for authenticated users)
  const userParishes = user?.parishes || [];
  const isAdminOfThisParish = !isPublic && (user?.is_site_admin || 
    userParishes.some(p => p.parish_id === targetParishId && p.is_admin));
  
  // Check if user is a member of this parish
  const isMemberOfThisParish = !isPublic && (user?.parish_id === targetParishId || 
    userParishes.some(p => p.parish_id === targetParishId));
  
  // Check if request is pending
  const isRequestPending = pendingRequests.includes(targetParishId);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load pending requests (only for authenticated users)
      if (!isPublic && user) {
        try {
          const pendingRes = await getMyPendingParishRequests();
          setPendingRequests(pendingRes.data.pending_requests || []);
        } catch (e) {
          console.error("Failed to load pending requests:", e);
        }
      }
      
      if (targetParishId) {
        // For public users, use the public parish events endpoint
        if (isPublic) {
          const [pRes, eRes] = await Promise.all([
            getParish(targetParishId),
            getParishEvents(targetParishId)
          ]);
          setParish(pRes.data);
          // Filter to only show future events
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          const futureEvents = (eRes.data || []).filter(e => new Date(e.event_date) >= now);
          setEvents(futureEvents);
        } else {
          const [pRes, eRes, mRes, recRes] = await Promise.all([
            getParish(targetParishId), 
            getParishEvents(targetParishId),
            getParishMembers(targetParishId),
            getRecurringEvents(targetParishId)
          ]);
          setParish(pRes.data);
          // Filter to only show future events
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          const futureEvents = (eRes.data || []).filter(e => new Date(e.event_date) >= now);
          setEvents(futureEvents);
          setMembers(mRes.data.members || []);
          setRecurringEvents(recRes.data.recurring_events || []);
          // Load parish posts
          try {
            const postsRes = await getParishPosts(targetParishId);
            setParishPosts(postsRes.data.posts || []);
          } catch (e) { console.error("Failed to load parish posts:", e); }
        }
      } else {
        const [pRes, eRes] = await Promise.all([getParishes(), getEvents("global")]);
        const parishData = pRes.data;
        setAllParishes(Array.isArray(parishData) ? parishData : (parishData.parishes || []));
        // Filter to only show future events
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const futureEvents = (eRes.data || []).filter(e => new Date(e.event_date) >= now);
        setEvents(futureEvents);
        setRecurringEvents([]);
      }
    } catch (err) {
      console.error("Failed to load parish data:", err);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [targetParishId]);

  useEffect(() => {
    if (user) {
      getMyBusinessListings().then(r => setUserBusinesses(r.data.listings || [])).catch(() => {});
    }
  }, [user]);

  const handleCreateParishPost = async () => {
    if (!newPostContent.trim()) return;
    setPostingToParish(true);
    try {
      let image_url = null;
      if (newPostImage) {
        const uploadRes = await uploadImage(newPostImage);
        image_url = uploadRes.data?.url || uploadRes.data?.data?.url;
      }
      const postPayload = {
        content: newPostContent,
        scope: "parish",
        parish_id: targetParishId,
        image_url,
        post_as_admin: postAsAdmin,
      };
      if (postAsBusiness) postPayload.business_listing_id = postAsBusiness;
      await createPost(postPayload);
      setNewPostContent("");
      setNewPostImage(null);
      setPostAsAdmin(false);
      setPostAsBusiness(null);
      toast.success("Posted to parish!");
      // Reload parish posts
      const postsRes = await getParishPosts(targetParishId);
      setParishPosts(postsRes.data.posts || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create post");
    }
    setPostingToParish(false);
  };

  const handleBannerFileUpload = async (file) => {
    if (!file) return;
    setUploadingBanner(true);
    try {
      const uploadRes = await uploadImage(file);
      const url = uploadRes.data?.url || uploadRes.data?.data?.url;
      if (!url) throw new Error("Upload failed");
      await updateParish(targetParishId, { banner_image_url: url });
      setParish(prev => ({ ...prev, banner_image_url: url }));
      toast.success("Banner image updated!");
      setBannerDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update banner");
    }
    setUploadingBanner(false);
  };

  const handleBannerUpdate = async () => {
    if (!bannerUrl.trim()) {
      toast.error("Please enter a banner image URL");
      return;
    }
    setUploadingBanner(true);
    try {
      await updateParish(targetParishId, { banner_image_url: bannerUrl });
      setParish(prev => ({ ...prev, banner_image_url: bannerUrl }));
      toast.success("Banner image updated!");
      setBannerDialogOpen(false);
      setBannerUrl("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update banner");
    }
    setUploadingBanner(false);
  };

  const handleLikePost = async (postId) => {
    try {
      const res = await likePost(postId);
      setParishPosts(prev => prev.map(p => p.post_id === postId ? {
        ...p,
        user_liked: res.data.liked,
        like_count: res.data.like_count
      } : p));
    } catch {}
  };

  const handleEditPost = async (postId, content) => {
    await updatePost(postId, { content });
    const postsRes = await getParishPosts(targetParishId);
    setParishPosts(postsRes.data.posts || []);
  };

  const handleDeletePost = async (postId) => {
    await deletePost(postId);
    setParishPosts(prev => prev.filter(p => p.post_id !== postId));
  };

  const handleRsvp = async (eventId, status) => {
    try {
      const res = await rsvpEvent(eventId, status);
      setEvents(events.map(e => e.event_id === eventId ? {
        ...e,
        user_rsvp: res.data.status,
        rsvp_count: res.data.status === "going" 
          ? (e.user_rsvp === "going" ? e.rsvp_count : (e.rsvp_count || 0) + 1)
          : (e.user_rsvp === "going" ? Math.max((e.rsvp_count || 0) - 1, 0) : e.rsvp_count),
        maybe_count: res.data.status === "maybe"
          ? (e.user_rsvp === "maybe" ? e.maybe_count : (e.maybe_count || 0) + 1)
          : (e.user_rsvp === "maybe" ? Math.max((e.maybe_count || 0) - 1, 0) : e.maybe_count)
      } : e));
      toast.success(res.data.status ? `RSVP: ${res.data.status}` : "RSVP removed");
    } catch {
      toast.error("Failed to RSVP");
    }
  };

  // Event editing state
  const [editingEvent, setEditingEvent] = useState(null);

  const handleEditEvent = (event) => {
    setEditingEvent(event);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      await deleteEvent(eventId);
      setEvents(events.filter(e => e.event_id !== eventId));
      toast.success("Event deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete event");
    }
  };

  // Recurring Events handlers
  const [editingRecurringEvent, setEditingRecurringEvent] = useState(null);
  const [showAddRecurring, setShowAddRecurring] = useState(false);
  const [viewingRecurringEvent, setViewingRecurringEvent] = useState(null);

  const handleDeleteRecurringEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this recurring event?")) return;
    try {
      await deleteRecurringEvent(eventId);
      setRecurringEvents(recurringEvents.filter(e => e.recurring_event_id !== eventId));
      toast.success("Recurring event deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const handleRequestJoin = async (parishId) => {
    setRequestingJoin(true);
    try {
      await requestMembership(parishId);
      toast.success("Request sent! Awaiting parish admin approval.");
      setPendingRequests([...pendingRequests, parishId]);
    } catch (err) {
      const message = err.response?.data?.detail || "Request failed";
      if (message.includes("already pending")) {
        toast.info("You already have a pending request for this parish");
        setPendingRequests([...pendingRequests, parishId]);
      } else if (message.includes("already a member")) {
        toast.info("You're already a member of this parish");
      } else {
        toast.error(message);
      }
    }
    setRequestingJoin(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]"><div className="w-8 h-8 border-2 border-[var(--liturgical-primary)] border-t-transparent rounded-full animate-spin" /></div>;

  // No parish to display - show discovery (only if no URL param and user has no parish)
  if (!targetParishId) {
    return (
      <div className="min-h-screen bg-[#F9F7F2]" data-testid="parish-discovery-page">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-['Crimson_Text'] text-4xl font-bold text-[#2D2A26] tracking-tight">Find Your Parish</h1>
              <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">Join a parish community or register a new one</p>
            </div>
            <CreateParishDialog user={user} onCreated={loadData} />
          </div>
          <div className="grid gap-4">
            {allParishes.map(p => (
              <Card key={p.parish_id} className="bg-white border-[#E5E0D5] shadow-sm hover:shadow-md transition-all" data-testid={`parish-discover-${p.parish_id}`}>
                <CardContent className="p-5 flex items-center gap-5">
                  <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0">
                    <img src={p.image_url || DEFAULT_PARISH_BANNER} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">{p.name}</h3>
                    <p className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{p.address || "Location not available"}</p>
                    <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1 line-clamp-1">{p.description}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-[#6B665E] font-['Nunito'] mb-2"><Users className="w-3 h-3 inline mr-1" />{p.member_count} members</p>
                    <Button size="sm" onClick={() => handleRequestJoin(p.parish_id)} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] text-xs" data-testid={`join-parish-${p.parish_id}`}>
                      Request to Join
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="parish-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        {/* Public Access Banner */}
        {isPublic && (
          <MembershipBanner context="parish" className="mb-6" />
        )}
        
        {/* Parish Header */}
        {parish && (
          <div className="relative mb-8">
            <div className="h-48 rounded-lg overflow-hidden">
              <img src={parish.banner_image_url || parish.image_url || DEFAULT_PARISH_BANNER} alt={parish.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-lg" />
            </div>
            <div className="absolute bottom-4 left-6 text-white">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="font-['Crimson_Text'] text-3xl font-bold tracking-tight" data-testid="parish-name">{parish.name}</h1>
                {parish.rite && (
                  <Badge className="bg-[var(--liturgical-secondary)] text-white border-0 text-[10px] font-['Nunito'] font-semibold" data-testid="parish-rite-badge">
                    <BookOpen className="w-3 h-3 mr-1" />{parish.rite}
                  </Badge>
                )}
              </div>
              <p className="text-sm font-['Nunito'] flex items-center gap-1.5 opacity-90"><MapPin className="w-3.5 h-3.5" />{parish.address || "Location not available"}</p>
            </div>
            <div className="absolute top-4 right-4 flex items-center gap-2">
              {/* Edit Banner Button - Admin only */}
              {isAdminOfThisParish && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setBannerDialogOpen(true)}
                  className="bg-white/90 hover:bg-white text-[#2D2A26] h-8 px-2"
                  data-testid="edit-banner-btn"
                >
                  <Camera className="w-4 h-4 mr-1" />
                  Edit Banner
                </Button>
              )}
              <Badge className="bg-white/90 text-[#2D2A26] font-['Nunito'] text-xs">
                <Users className="w-3 h-3 mr-1" />{parish.member_count} Members
              </Badge>
              {isMemberOfThisParish && (
                <Badge className="bg-green-500/90 text-white font-['Nunito'] text-xs">
                  <Check className="w-3 h-3 mr-1" />Member
                </Badge>
              )}
            </div>
            {/* Request to Join button - show only for authenticated non-members */}
            {!isPublic && !isMemberOfThisParish && (
              <div className="absolute bottom-4 right-6">
                {isRequestPending ? (
                  <Badge className="bg-yellow-500 text-white font-['Nunito'] text-sm py-1.5 px-3">
                    <Clock className="w-3 h-3 mr-1" />Request Pending
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleRequestJoin(targetParishId)}
                    disabled={requestingJoin}
                    className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] shadow-lg"
                    data-testid="request-join-parish-btn"
                  >
                    {requestingJoin ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <UserPlus className="w-4 h-4 mr-1" />
                    )}
                    Request to Join
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} data-testid="parish-tabs">
          <TabsList className="bg-[#F0EBE0] border border-[#E5E0D5] mb-6 flex-wrap h-auto">
            <TabsTrigger value="about" className="font-['Nunito'] text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]">About</TabsTrigger>
            <TabsTrigger value="posts" className="font-['Nunito'] text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]">Posts</TabsTrigger>
            <TabsTrigger value="sacraments" className="font-['Nunito'] text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]">Sacraments</TabsTrigger>
            <TabsTrigger value="events" className="font-['Nunito'] text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]">Events</TabsTrigger>
            <TabsTrigger value="challenges" className="font-['Nunito'] text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]" data-testid="parish-challenges-tab">Challenges</TabsTrigger>
            {!isPublic && (
              <TabsTrigger value="members" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]">Members</TabsTrigger>
            )}
            {isAdminOfThisParish && (
              <TabsTrigger value="admin" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]">
                <Settings className="w-3.5 h-3.5 mr-1.5" />
                Parish Admin
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="about">
            <div className="space-y-4">
              {/* Description */}
              <Card className="bg-white border-[#E5E0D5] shadow-sm">
                <CardContent className="p-6">
                  <h2 className="font-['Crimson_Text'] text-2xl font-semibold text-[#2D2A26] mb-3">About Our Parish</h2>
                  <SafeHTML
                    html={parish?.description || ''}
                    className="text-sm text-[#6B665E] font-['Nunito'] leading-relaxed prose prose-sm max-w-none break-words overflow-hidden"
                  />
                </CardContent>
              </Card>

              {/* Parish Staff & Clergy */}
              {parish?.staff && parish.staff.length > 0 && (
                <Card className="bg-white border-[#E5E0D5] shadow-sm" data-testid="parish-staff-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Users className="w-5 h-5 text-[var(--liturgical-primary)]" />
                      <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">Parish Staff & Clergy</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {parish.staff.map((member, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center gap-3 p-3 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5]"
                        >
                          <Avatar className="h-12 w-12 border-2 border-[var(--liturgical-primary)]/20">
                            {member.linked_member?.profile_picture ? (
                              <AvatarImage src={member.linked_member.profile_picture} />
                            ) : (
                              <AvatarFallback className="bg-[var(--liturgical-primary)] text-white">
                                {member.name?.[0]?.toUpperCase()}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">
                              {member.title && `${member.title} `}{member.name}
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={`text-[10px] ${
                                member.role === 'pastor' ? 'bg-purple-100 text-purple-700' :
                                member.role === 'parochial_vicar' ? 'bg-blue-100 text-blue-700' :
                                member.role === 'deacon' ? 'bg-green-100 text-green-700' :
                                member.role === 'religious' ? 'bg-pink-100 text-pink-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {member.role === 'pastor' ? 'Pastor' :
                                 member.role === 'parochial_vicar' ? 'Parochial Vicar' :
                                 member.role === 'deacon' ? 'Deacon' :
                                 member.role === 'religious' ? 'Religious' :
                                 'Staff'}
                              </Badge>
                              {member.user_id && (
                                <Badge variant="outline" className="text-[10px] border-green-300 text-green-700 bg-green-50">
                                  <Link2 className="w-2.5 h-2.5 mr-1" />
                                  Member
                                </Badge>
                              )}
                            </div>
                            {(member.email || member.phone) && (
                              <div className="flex items-center gap-3 mt-1 text-[10px] text-[#6B665E] font-['Nunito']">
                                {member.email && (
                                  <a href={`mailto:${member.email}`} className="flex items-center gap-0.5 hover:text-[var(--liturgical-primary)]">
                                    <Mail className="w-2.5 h-2.5" />
                                    {member.email}
                                  </a>
                                )}
                                {member.phone && (
                                  <a href={`tel:${member.phone}`} className="flex items-center gap-0.5 hover:text-[var(--liturgical-primary)]">
                                    <Phone className="w-2.5 h-2.5" />
                                    {member.phone}
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Parish Admins */}
              {parish?.admins && parish.admins.length > 0 && (
                <Card className="bg-white border-[#E5E0D5] shadow-sm" data-testid="parish-admins-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Shield className="w-5 h-5 text-[var(--liturgical-primary)]" />
                      <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">Parish Administrators</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {parish.admins.map((admin) => (
                        <div 
                          key={admin.user_id} 
                          className="flex items-center gap-3 p-3 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5]"
                          data-testid={`parish-admin-${admin.user_id}`}
                        >
                          <Avatar className="h-10 w-10 border-2 border-[var(--liturgical-primary)]/30">
                            <AvatarImage src={admin.picture} />
                            <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm">
                              {admin.name?.[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-['Nunito'] text-sm font-semibold text-[#2D2A26] truncate">{admin.name}</p>
                              {admin.user_id === parish.primary_admin_id && (
                                <Badge className="bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)] border-0 text-[10px] font-['Nunito'] flex-shrink-0">
                                  Primary
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-[#6B665E] font-['Nunito'] truncate">{admin.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Contact Information */}
              <Card className="bg-white border-[#E5E0D5] shadow-sm">
                <CardContent className="p-6">
                  <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26] mb-4">Contact Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Address */}
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[var(--liturgical-primary)]/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-[var(--liturgical-primary)]" />
                      </div>
                      <div>
                        <p className="text-xs text-[#6B665E] font-['Nunito'] font-semibold uppercase tracking-wide">Address</p>
                        <p className="text-sm text-[#2D2A26] font-['Nunito']">{parish?.address || parish?.location || "Not provided"}</p>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[var(--liturgical-primary)]/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-4 h-4 text-[var(--liturgical-primary)]" />
                      </div>
                      <div>
                        <p className="text-xs text-[#6B665E] font-['Nunito'] font-semibold uppercase tracking-wide">Email</p>
                        {parish?.office_email ? (
                          <a href={`mailto:${parish.office_email}`} className="text-sm text-[var(--liturgical-primary)] font-['Nunito'] hover:underline">
                            {parish.office_email}
                          </a>
                        ) : (
                          <p className="text-sm text-[#6B665E] font-['Nunito']">Not provided</p>
                        )}
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[var(--liturgical-secondary)]/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-4 h-4 text-[var(--liturgical-secondary)]" />
                      </div>
                      <div>
                        <p className="text-xs text-[#6B665E] font-['Nunito'] font-semibold uppercase tracking-wide">Phone</p>
                        {parish?.office_phone ? (
                          <a href={`tel:${parish.office_phone}`} className="text-sm text-[var(--liturgical-primary)] font-['Nunito'] hover:underline">
                            {parish.office_phone}
                          </a>
                        ) : (
                          <p className="text-sm text-[#6B665E] font-['Nunito']">Not provided</p>
                        )}
                      </div>
                    </div>

                    {/* Website */}
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#6B665E]/10 flex items-center justify-center flex-shrink-0">
                        <ExternalLink className="w-4 h-4 text-[#6B665E]" />
                      </div>
                      <div>
                        <p className="text-xs text-[#6B665E] font-['Nunito'] font-semibold uppercase tracking-wide">Website</p>
                        {parish?.website ? (
                          <a href={parish.website} target="_blank" rel="noopener noreferrer" className="text-sm text-[var(--liturgical-primary)] font-['Nunito'] hover:underline">
                            {parish.website.replace(/^https?:\/\//, '')}
                          </a>
                        ) : (
                          <p className="text-sm text-[#6B665E] font-['Nunito']">Not provided</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Posts Tab */}
          <TabsContent value="posts">
            <div className="space-y-4 max-w-2xl">
              {/* Create Post - All members */}
              {isMemberOfThisParish && (
                <Card className="bg-white border-[#E5E0D5] shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex-1 space-y-3">
                      {/* Post as selector */}
                      {(userBusinesses.length > 0 || isAdminOfThisParish) && (
                        <Select value={postAsAdmin ? "parish_admin" : postAsBusiness || "personal"} onValueChange={v => {
                          if (v === "parish_admin") { setPostAsAdmin(true); setPostAsBusiness(null); }
                          else if (v === "personal") { setPostAsAdmin(false); setPostAsBusiness(null); }
                          else { setPostAsAdmin(false); setPostAsBusiness(v); }
                        }}>
                          <SelectTrigger className="w-auto h-7 border-[#E5E0D5] text-xs font-['Nunito'] px-2 inline-flex gap-1" data-testid="parish-post-as-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-[#E5E0D5]">
                            <SelectItem value="personal" className="text-xs font-['Nunito']">
                              <span className="flex items-center gap-1.5">{user.name || "Personal"}</span>
                            </SelectItem>
                            {isAdminOfThisParish && (
                              <SelectItem value="parish_admin" className="text-xs font-['Nunito']">
                                <span className="flex items-center gap-1.5"><Church className="w-3 h-3" />Parish Admin</span>
                              </SelectItem>
                            )}
                            {userBusinesses.map(b => (
                              <SelectItem key={b.listing_id} value={b.listing_id} className="text-xs font-['Nunito']">
                                <span className="flex items-center gap-1.5"><Building2 className="w-3 h-3" />{b.name}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <textarea
                        value={newPostContent}
                        onChange={(e) => {
                          setNewPostContent(e.target.value);
                          e.target.style.height = "auto";
                          e.target.style.height = e.target.scrollHeight + "px";
                        }}
                        placeholder="Share with your parish..."
                        className="w-full p-3 text-sm font-['Nunito'] text-[#2D2A26] bg-[#F9F7F2] border border-[#E5E0D5] rounded-lg resize-none min-h-[80px] max-h-[400px] overflow-y-auto focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)]/30"
                        data-testid="parish-post-input"
                      />
                      {newPostImage && (
                        <div className="relative inline-block">
                          <img src={URL.createObjectURL(newPostImage)} alt="Upload preview" className="max-h-32 rounded-lg border border-[#E5E0D5]" />
                          <button onClick={() => setNewPostImage(null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <label className="cursor-pointer p-1.5 rounded-md text-[#6B665E] hover:text-[var(--liturgical-primary)] hover:bg-[#F0EBE0] transition-colors" title="Add image">
                            <ImagePlus className="w-5 h-5" />
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => setNewPostImage(e.target.files?.[0] || null)} />
                          </label>
                        </div>
                        <Button
                          onClick={handleCreateParishPost}
                          disabled={postingToParish || !newPostContent.trim()}
                          className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] text-sm"
                          data-testid="parish-post-submit"
                        >
                          {postingToParish ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Post</>}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Parish Posts Feed */}
              {parishPosts.length === 0 ? (
                <Card className="border-[#E5E0D5]">
                  <CardContent className="py-12 text-center">
                    <MessageCircle className="w-12 h-12 mx-auto text-[#6B665E] mb-4" />
                    <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">No parish posts yet</h3>
                    <p className="text-[#6B665E] font-['Nunito']">
                      {isMemberOfThisParish ? "Share the first update with your parish above." : "Join this parish to see and create posts."}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                parishPosts.map(post => (
                  <PostCard
                    key={post.post_id}
                    post={post}
                    currentUserId={user?.user_id}
                    onLike={handleLikePost}
                    onComment={() => {}}
                    onEdit={handleEditPost}
                    onDelete={handleDeletePost}
                  />
                ))
              )}
            </div>
          </TabsContent>

          {/* Sacraments Tab */}
          <TabsContent value="sacraments">
            <div className="space-y-4">
              {/* Mass Schedules */}
              <Card className="bg-white border-[#E5E0D5] shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Church className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">Holy Mass Schedule</h2>
                  </div>
                  {parish?.mass_schedules && parish.mass_schedules.length > 0 ? (
                    <div className="space-y-3">
                      {parish.mass_schedules.map((schedule, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-md border border-[#E5E0D5]">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[var(--liturgical-primary)]/10 rounded-md flex items-center justify-center">
                              <CalendarDays className="w-5 h-5 text-[var(--liturgical-primary)]" />
                            </div>
                            <div>
                              <p className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">{schedule.day}</p>
                              <p className="text-xs text-[#6B665E] font-['Nunito']">{schedule.time}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className="bg-[var(--liturgical-secondary)]/10 text-[var(--liturgical-secondary)] border-[var(--liturgical-secondary)] text-[10px] font-['Nunito']">
                              {schedule.liturgy_type || "Novus Ordo"}
                            </Badge>
                            {schedule.language && schedule.language !== "English" && (
                              <p className="text-[10px] text-[#6B665E] font-['Nunito'] mt-1">{schedule.language}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Church className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
                      <p className="text-sm text-[#6B665E] font-['Nunito']">No Mass schedules posted yet</p>
                      <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">Parish admins can add schedules from the admin dashboard</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Reconciliation Schedules */}
              <Card className="bg-white border-[#E5E0D5] shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Cross className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">Reconciliation (Confession)</h2>
                  </div>
                  {parish?.reconciliation_schedules && parish.reconciliation_schedules.length > 0 ? (
                    <div className="space-y-3">
                      {parish.reconciliation_schedules.map((schedule, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-md border border-[#E5E0D5]">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[var(--liturgical-primary)]/10 rounded-md flex items-center justify-center">
                              <CalendarDays className="w-5 h-5 text-[var(--liturgical-primary)]" />
                            </div>
                            <div>
                              <p className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">{schedule.day}</p>
                              <p className="text-xs text-[#6B665E] font-['Nunito']">{schedule.start_time} - {schedule.end_time}</p>
                            </div>
                          </div>
                          {schedule.notes && (
                            <p className="text-xs text-[#6B665E] font-['Nunito'] italic">{schedule.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Cross className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
                      <p className="text-sm text-[#6B665E] font-['Nunito']">No Reconciliation schedules posted yet</p>
                      <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">Parish admins can add schedules from the admin dashboard</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Adoration Schedules */}
              <Card className="bg-white border-[#E5E0D5] shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Sun className="w-5 h-5 text-[var(--liturgical-secondary)]" />
                    <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">Eucharistic Adoration</h2>
                  </div>
                  {parish?.adoration_schedules && parish.adoration_schedules.length > 0 ? (
                    <div className="space-y-3">
                      {parish.adoration_schedules.map((schedule, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-md border border-[#E5E0D5]">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[var(--liturgical-secondary)]/10 rounded-md flex items-center justify-center">
                              <CalendarDays className="w-5 h-5 text-[var(--liturgical-secondary)]" />
                            </div>
                            <div>
                              <p className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">{schedule.day}</p>
                              <p className="text-xs text-[#6B665E] font-['Nunito']">{schedule.start_time} - {schedule.end_time}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {schedule.perpetual && (
                              <Badge className="bg-[var(--liturgical-secondary)]/10 text-[var(--liturgical-secondary)] border-[var(--liturgical-secondary)] text-[10px] font-['Nunito']">
                                Perpetual
                              </Badge>
                            )}
                            {schedule.notes && (
                              <p className="text-xs text-[#6B665E] font-['Nunito'] italic">{schedule.notes}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Sun className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
                      <p className="text-sm text-[#6B665E] font-['Nunito']">No Adoration schedules posted yet</p>
                      <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">Parish admins can add schedules from the admin dashboard</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="events">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Upcoming Events - Left/Main Column */}
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-['Crimson_Text'] text-2xl font-semibold text-[#2D2A26]">Upcoming Events</h2>
                  {isAdminOfThisParish && (
                    <CreateEventDialog user={user} onCreated={loadData} parishId={targetParishId} />
                  )}
                </div>
                <div className="space-y-3">
                  {events.length === 0 ? (
                    <div className="text-center py-12 text-[#6B665E] font-['Nunito']" data-testid="no-events">
                      <CalendarDays className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
                      No upcoming events
                    </div>
                  ) : (
                    events.map(event => (
                      <EventCard 
                        key={event.event_id} 
                        event={event} 
                        onRsvp={handleRsvp}
                        canEdit={isParishAdmin(user, event.parish_id)}
                        onEdit={handleEditEvent}
                        onDelete={handleDeleteEvent}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Recurring Events - Right Sidebar */}
              <div className="lg:col-span-1">
                <Card className="bg-white border-[#E5E0D5] shadow-sm sticky top-4">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-['Crimson_Text'] text-lg flex items-center gap-2">
                        <Repeat className="w-4 h-4 text-[var(--liturgical-primary)]" />
                        Recurring Events
                      </CardTitle>
                      {isAdminOfThisParish && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10"
                          onClick={() => setShowAddRecurring(true)}
                          data-testid="add-recurring-event-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {recurringEvents.length === 0 ? (
                      <p className="text-sm text-[#6B665E] font-['Nunito'] text-center py-4">
                        No recurring events
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {recurringEvents.map(event => (
                          <div 
                            key={event.recurring_event_id} 
                            className="relative p-3 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5]/50 cursor-pointer hover:bg-[#F0EBE0] hover:border-[var(--liturgical-primary)]/30 transition-all"
                            data-testid={`recurring-event-${event.recurring_event_id}`}
                            onClick={() => setViewingRecurringEvent(event)}
                          >
                            {isAdminOfThisParish && (
                              <div className="absolute top-2 right-2 flex gap-1" onClick={e => e.stopPropagation()}>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-[#6B665E] hover:text-[var(--liturgical-primary)] hover:bg-white/50"
                                  onClick={() => setEditingRecurringEvent(event)}
                                >
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-[#6B665E] hover:text-red-600 hover:bg-white/50"
                                  onClick={() => handleDeleteRecurringEvent(event.recurring_event_id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                            <h4 className="font-['Nunito'] font-semibold text-sm text-[#2D2A26] pr-14">{event.title}</h4>
                            <p className="text-xs text-[#6B665E] font-['Nunito'] mt-0.5 line-clamp-2">{event.description}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-[#6B665E] font-['Nunito']">
                              <Badge variant="outline" className="border-[var(--liturgical-primary)]/30 text-[var(--liturgical-primary)] text-[10px] py-0">
                                <Repeat className="w-2.5 h-2.5 mr-1" />
                                {event.schedule}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 mt-1.5 text-xs text-[#6B665E] font-['Nunito']">
                              <span className="flex items-center gap-1 whitespace-nowrap"><Clock className="w-3 h-3 flex-shrink-0" />{event.time}</span>
                              <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3 flex-shrink-0" /><span className="truncate">{event.location}</span></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Edit Event Dialog */}
            {editingEvent && (
              <EditEventDialog
                event={editingEvent}
                onUpdated={loadData}
                onClose={() => setEditingEvent(null)}
              />
            )}

            {/* Add Recurring Event Dialog */}
            {showAddRecurring && (
              <AddRecurringEventDialog
                parishId={targetParishId}
                onCreated={loadData}
                onClose={() => setShowAddRecurring(false)}
              />
            )}

            {/* Edit Recurring Event Dialog */}
            {editingRecurringEvent && (
              <EditRecurringEventDialog
                event={editingRecurringEvent}
                onUpdated={loadData}
                onClose={() => setEditingRecurringEvent(null)}
              />
            )}

            {/* View Recurring Event Dialog */}
            {viewingRecurringEvent && (
              <ViewRecurringEventDialog
                event={viewingRecurringEvent}
                onClose={() => setViewingRecurringEvent(null)}
              />
            )}
          </TabsContent>

          <TabsContent value="challenges">
            <ChallengesTab parishId={parish?.parish_id} />
          </TabsContent>

          <TabsContent value="members">
            <h2 className="font-['Crimson_Text'] text-2xl font-semibold text-[#2D2A26] mb-4">Parish Members</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {members.length > 0 ? (
                members.map(m => (
                  <Card key={m.user_id} className="bg-white border-[#E5E0D5] shadow-sm" data-testid={`member-card-${m.user_id}`}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <Avatar className="h-10 w-10 border border-[#E5E0D5]">
                        <AvatarImage src={m.picture} />
                        <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm">{m.name?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">{m.name}</p>
                        <p className="text-xs text-[#6B665E] font-['Nunito']">{m.email}</p>
                      </div>
                      {(m.role === "admin" || m.role === "parish_admin") && (
                        <Badge className="bg-[var(--liturgical-secondary)]/10 text-[var(--liturgical-secondary)] border-[var(--liturgical-secondary)] text-[10px]">
                          {m.role === "admin" ? "App Admin" : "Parish Admin"}
                        </Badge>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="col-span-2 text-center py-8">
                  <Users className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
                  <p className="text-sm text-[#6B665E] font-['Nunito']">No members yet</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Parish Admin Tab - Only visible to parish admins */}
          {isAdminOfThisParish && (
            <TabsContent value="admin">
              <Card className="bg-white border-[#E5E0D5] shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
                      <Settings className="w-6 h-6 text-[var(--liturgical-primary)]" />
                    </div>
                    <div>
                      <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">Parish Administration</h2>
                      <p className="text-sm text-[#6B665E] font-['Nunito']">Manage your parish settings, members, and content</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Button
                      variant="outline"
                      className="h-auto py-4 px-5 flex items-center gap-3 border-[#E5E0D5] hover:bg-[var(--liturgical-primary)]/5 hover:border-[var(--liturgical-primary)]/30 transition-all justify-start"
                      onClick={() => navigate(`/parish-admin?parish=${targetParishId}`)}
                      data-testid="admin-dashboard-btn"
                    >
                      <div className="w-10 h-10 rounded-full bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-[var(--liturgical-primary)]" />
                      </div>
                      <div className="text-left">
                        <span className="block text-sm font-['Nunito'] font-semibold text-[#2D2A26]">Admin Dashboard</span>
                        <span className="block text-xs font-['Nunito'] text-[#6B665E]">Manage members & requests</span>
                      </div>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-auto py-4 px-5 flex items-center gap-3 border-[#E5E0D5] hover:bg-[var(--liturgical-primary)]/5 hover:border-[var(--liturgical-primary)]/30 transition-all justify-start"
                      onClick={() => setTab('events')}
                      data-testid="admin-events-btn"
                    >
                      <div className="w-10 h-10 rounded-full bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
                        <CalendarDays className="w-5 h-5 text-[var(--liturgical-primary)]" />
                      </div>
                      <div className="text-left">
                        <span className="block text-sm font-['Nunito'] font-semibold text-[#2D2A26]">Manage Events</span>
                        <span className="block text-xs font-['Nunito'] text-[#6B665E]">Create & edit parish events</span>
                      </div>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-auto py-4 px-5 flex items-center gap-3 border-[#E5E0D5] hover:bg-[var(--liturgical-primary)]/5 hover:border-[var(--liturgical-primary)]/30 transition-all justify-start"
                      onClick={() => navigate(`/parish-admin?parish=${targetParishId}&tab=sacraments`)}
                      data-testid="admin-sacraments-btn"
                    >
                      <div className="w-10 h-10 rounded-full bg-[var(--liturgical-secondary)]/10 flex items-center justify-center">
                        <Cross className="w-5 h-5 text-[var(--liturgical-secondary)]" />
                      </div>
                      <div className="text-left">
                        <span className="block text-sm font-['Nunito'] font-semibold text-[#2D2A26]">Edit Sacraments</span>
                        <span className="block text-xs font-['Nunito'] text-[#6B665E]">Update Mass & confession times</span>
                      </div>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Edit Banner Dialog */}
      <Dialog open={bannerDialogOpen} onOpenChange={setBannerDialogOpen}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl flex items-center gap-2">
              <Camera className="w-5 h-5 text-[var(--liturgical-primary)]" />
              Change Banner Image
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* File Upload */}
            <div>
              <label className="text-sm font-medium text-[#2D2A26] block mb-2 font-['Nunito']">
                Upload Image
              </label>
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#E5E0D5] rounded-lg cursor-pointer hover:border-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/5 transition-colors" data-testid="banner-file-upload">
                <div className="flex flex-col items-center justify-center py-4">
                  <ImagePlus className="w-8 h-8 text-[#6B665E] mb-2" />
                  <p className="text-sm text-[#6B665E] font-['Nunito']">Click to upload a banner image</p>
                  <p className="text-xs text-[#9B9590] font-['Nunito'] mt-1">Recommended: 1200x400px</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleBannerFileUpload(file);
                  }}
                />
              </label>
            </div>

            {/* OR divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t border-[#E5E0D5]" />
              <span className="text-xs text-[#6B665E] font-['Nunito']">OR</span>
              <div className="flex-1 border-t border-[#E5E0D5]" />
            </div>

            {/* URL Input */}
            <div>
              <label className="text-sm font-medium text-[#2D2A26] block mb-2 font-['Nunito']">
                Image URL
              </label>
              <div className="flex gap-2">
                <Input
                  placeholder="https://example.com/image.jpg"
                  value={bannerUrl}
                  onChange={(e) => setBannerUrl(e.target.value)}
                  className="border-[#E5E0D5] flex-1"
                  data-testid="banner-url-input"
                />
                <Button 
                  onClick={handleBannerUpdate}
                  disabled={uploadingBanner || !bannerUrl.trim()}
                  className="bg-[var(--liturgical-primary)] hover:brightness-90"
                  data-testid="save-banner-url-btn"
                >
                  Apply
                </Button>
              </div>
            </div>

            {uploadingBanner && (
              <div className="flex items-center justify-center py-4 gap-2 text-[var(--liturgical-primary)]">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm font-['Nunito']">Uploading banner...</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
